import { useState, useEffect } from 'react';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';

interface BillingSummary {
  billing_month: string;
  total_delivered_logs: number;
  total_customers: number;
  already_billed_customers: number;
  pending_customers: number;
  estimated_total_amount: number;
}

interface BillingResult {
  billing_month: string;
  invoices_created: number;
  invoices_skipped: number;
  total_amount: number;
  errors: string[];
}

function getLastMonths(n: number) {
  const months = [];
  const d = new Date();
  for (let i = 1; i <= n; i++) {
    d.setMonth(d.getMonth() - 1);
    months.push(d.toISOString().slice(0, 7)); // YYYY-MM
  }
  return months;
}

export default function SubscriptionBillingPage() {
  const lastMonth = getLastMonths(1)[0];

  const [selectedMonth, setSelectedMonth] = useState(lastMonth);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [result, setResult] = useState<BillingResult | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { success, error } = useToast();

  const fetchSummary = async (month: string) => {
    setLoadingSummary(true);
    setSummary(null);
    setResult(null);
    try {
      const res = await api.get('/delivery_billing/summary', { month });
      if (res.success) {
        setSummary(res.data);
      } else {
        error(res.message || 'Failed to load summary');
      }
    } catch { 
      error('Failed to load summary'); 
    } finally { 
      setLoadingSummary(false); 
    }
  };

  useEffect(() => { fetchSummary(selectedMonth); }, [selectedMonth]);

  const handleGenerate = async () => {
    if (!confirm(`Generate invoices for ${selectedMonth}? This will create invoices for all unbilled customers.`)) return;

    setGenerating(true);
    setResult(null);
    try {
      const res = await api.post('/delivery_billing/generate', { month: selectedMonth });
      if (res.success) {
        const d = res.data;
        setResult(d);
        if (d.invoices_created > 0) {
          success(`✓ ${d.invoices_created} invoice${d.invoices_created !== 1 ? 's' : ''} generated — ₹${d.total_amount?.toFixed(2)}`);
        } else {
          success('All customers already billed for this month');
        }
        fetchSummary(selectedMonth);
      } else {
        error(res.message || 'Failed to generate invoices');
      }
    } catch { 
      error('Failed to generate invoices'); 
    } finally { 
      setGenerating(false); 
    }
  };

  const months = [lastMonth, ...getLastMonths(5).slice(1)];

  return (
    <div className="pt-6 space-y-8 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-2 border-b border-slate-100">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Subscription <span className="text-indigo-600">Billing</span></h1>
          <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
             <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"></span>
             Process monthly commercial transactions for the operational cycle
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-600 group-focus-within:text-indigo-400">
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <select
              id="billing-month-select"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="pl-10 pr-10 py-3 bg-white border-2 border-slate-100 rounded-2xl text-sm text-slate-900 font-black focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 shadow-sm transition-all appearance-none"
            >
              {months.map(m => (
                <option key={m} value={m}>
                  {new Date(m + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Summary Stats */}
      {loadingSummary ? (
        <div className="py-12 flex flex-col items-center justify-center space-y-3">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Auditing Ledger Summary...</p>
        </div>
      ) : summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Delivered Logs', value: summary.total_delivered_logs, color: 'indigo', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { label: 'Total Customers', value: summary.total_customers, color: 'slate', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
            { label: 'Pending Billing', value: summary.pending_customers, color: 'amber', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Already Billed', value: summary.already_billed_customers, color: 'emerald', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
          ].map(s => (
            <div key={s.label} className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm shadow-slate-200/50 hover:shadow-xl hover:shadow-indigo-100/30 transition-all duration-500 group">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 duration-500 ${
                    s.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' :
                    s.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                    s.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                    'bg-slate-50 text-slate-600'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={s.icon} /></svg>
                  </div>
                  <span className="text-2xl font-black text-slate-900 tracking-tighter">{s.value}</span>
                </div>
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Commercial Yield Panel */}
      {summary && (
        <div className="bg-white rounded-[2.5rem] border-2 border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col lg:flex-row items-stretch">
          <div className="flex-1 p-10 bg-gradient-to-br from-indigo-50/50 via-white to-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-100/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
            <div className="relative z-10">
              <p className="text-indigo-600 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Estimated Commercial Yield</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-black text-slate-900 tracking-tighter">₹{summary.estimated_total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">Total Payable</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                  <span className="text-xs font-bold text-slate-600">{summary.pending_customers} customers awaiting ledger entry</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-indigo-400"></span>
                  <span className="text-xs font-bold text-slate-600">{selectedMonth} logic cycle</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-10 flex items-center justify-center bg-slate-50 border-l border-slate-100 lg:min-w-[340px]">
            <button
              id="generate-invoices-btn"
              onClick={handleGenerate}
              disabled={generating || summary.pending_customers === 0}
              className={`w-full group relative px-8 py-5 rounded-[1.5rem] font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 overflow-hidden ${
                generating || summary.pending_customers === 0
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed border-2 border-slate-200'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xl shadow-indigo-200 hover:-translate-y-1 active:scale-95 border-2 border-indigo-600'
              }`}
            >
              {generating ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Run Billing Engine
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Result panel */}
      {result && (
        <div className={`rounded-2xl border p-6 animate-in zoom-in-95 duration-300 ${result.errors.length ? 'border-amber-200 bg-amber-50 shadow-amber-50' : 'border-emerald-200 bg-emerald-50 shadow-emerald-50'}`}>
          <div className="flex items-center gap-4 mb-4">
             <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm ${result.errors.length ? 'bg-white text-amber-600' : 'bg-white text-emerald-600'}`}>
               {result.errors.length ? '⚠️' : '✅'}
             </div>
             <div>
                <h3 className={`font-black uppercase text-xs tracking-widest ${result.errors.length ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {result.errors.length ? 'Batch Warning' : 'Operation Successful'}
                </h3>
                <p className={`text-sm font-bold ${result.errors.length ? 'text-amber-700/70' : 'text-emerald-700/70'}`}>
                   Ledger has been synchronized with commercial logs
                </p>
             </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white/50 p-4 rounded-xl border border-white/50">
              <div className="text-xl font-black text-slate-800">{result.invoices_created}</div>
              <div className="text-[10px] text-slate-500 font-black uppercase tracking-wider">Invoices</div>
            </div>
            <div className="bg-white/50 p-4 rounded-xl border border-white/50">
              <div className="text-xl font-black text-slate-800">{result.invoices_skipped}</div>
              <div className="text-[10px] text-slate-500 font-black uppercase tracking-wider">Skipped</div>
            </div>
            <div className="bg-white/50 p-4 rounded-xl border border-white/50">
              <div className="text-xl font-black text-indigo-700">₹{result.total_amount.toLocaleString('en-IN')}</div>
              <div className="text-[10px] text-indigo-600 font-black uppercase tracking-wider">Total</div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-amber-100/50 rounded-xl p-4 border border-amber-200/50">
              <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-2">Partial Failure Context:</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-amber-700 font-medium">• {e}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Premium Protocol Callout */}
      <div className="bg-slate-900 rounded-[2rem] p-10 group relative overflow-hidden shadow-2xl shadow-slate-200">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-indigo-600/20 transition-all duration-700"></div>
        <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
          <div className="w-20 h-20 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl flex items-center justify-center text-4xl shadow-2xl animate-bounce duration-[3000ms]">💡</div>
          <div className="flex-1 space-y-3">
            <h3 className="text-xl font-black text-white tracking-tight">Ledger Protocol Intelligence</h3>
            <p className="text-slate-400 font-medium leading-relaxed max-w-3xl">
              Only deliveries with authorized status <strong className="text-white">Delivered</strong> are aggregated into month-end invoices. The billing engine is <strong className="text-white">idempotent</strong> — customers already processed within the selected chronology are automatically bypassed. Each entity receives a single consolidated commercial statement representing their monthly trajectory.
            </p>
            <div className="flex items-center gap-4 pt-2">
               <div className="flex items-center gap-1.5 px-3 py-1 bg-white/5 rounded-full border border-white/10">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Automation Ready</span>
               </div>
               <div className="flex items-center gap-1.5 px-3 py-1 bg-white/5 rounded-full border border-white/10">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">ISO 27001 Compliant</span>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
